import React, { useState, useEffect } from "react";
import { message, Radio } from "antd";
import ModalLayout from "../..";
import Button from "../../../button";
import "./style.scss";

interface ChangeStatusModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (data: { status: number; description: string }) => void;
  loading?: boolean;
  mode: "rejected" | "parked"; // restrict to these two modes
}

const STATUS_CODE_MAP: Record<string, number> = {
  rejected: 23,
  parked: 31,
};

// ✅ Reasons for each status
const REASON_MAP: Record<string, string[]> = {
  rejected: ["Not My Depot", "Not My Department"],
  parked: ["No Team Member", "No Vehicle", "No Tools"],
};

const ChangeStatusModal: React.FC<ChangeStatusModalProps> = ({
  open,
  onClose,
  onConfirm,
  mode,
  loading = false,
}) => {
  const [reason, setReason] = useState<string>("");

  useEffect(() => {
    if (open) {
      // ✅ Select first reason by default
      setReason(REASON_MAP[mode]?.[0] || "");
    }
  }, [open, mode]);

  const LABELS = {
    rejected: {
      title: "Reject Ticket",
      message: "You are about to reject this ticket.",
      reasonHeading: "Select a reason for rejection:",
    },
    parked: {
      title: "Park Ticket",
      message: "You are about to park this ticket.",
      reasonHeading: "Select a reason for holding (parked):",
    },
  };

  const handleConfirm = () => {
    if (!reason) {
      message.warning("Please select a reason before confirming.");
      return;
    }

    const statusCode = STATUS_CODE_MAP[mode];
    onConfirm({ status: statusCode, description: reason });
  };

  const footer = (
    <div className="confirm-modal-actions">
      <div className="spacer" />
      <Button className="ant-btn-tertiary" onClick={onClose} disabled={loading}>
        CANCEL
      </Button>
      <Button
        loading={loading}
        className="ant-btn-secondary"
        type="primary"
        onClick={handleConfirm}
      >
        CONFIRM
      </Button>
    </div>
  );

  return (
    <ModalLayout
      open={open}
      onClose={onClose}
      title={LABELS[mode]?.title || "Change Ticket Status"}
      footer={footer}
    >
      <div className="confirm-modal-wrapper flex flex-col gap-4">
        <h6 className="text-head">{LABELS[mode]?.message}</h6>

        <div className="reasons-section">
          <p className="reason-heading">{LABELS[mode]?.reasonHeading}</p>

          <Radio.Group
            onChange={(e) => setReason(e.target.value)}
            value={reason}
            className="reason-radio-group flex flex-col gap-2"
          >
            {REASON_MAP[mode]?.map((r) => (
              <Radio key={r} value={r}>
                {r}
              </Radio>
            ))}
          </Radio.Group>
        </div>
      </div>
    </ModalLayout>
  );
};

export default ChangeStatusModal;
